import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDrawer } from '../../../shared/drawer';
import { Table } from '../../../elements/table';
import { PageBody } from '../../../elements/page-body';
import { Button } from '../../../elements/button';
import { SearchIcon } from '@heroicons/react/outline';
import { CustomerForm } from './customers-form';
import { useHttpClient } from '../../../shared/http-client';

const FILTERS_STORAGE_KEY = 'customers-table-filters';

const getStoredFilters = () => {
    try {
        const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

export const CustomersTable = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { open, close } = useDrawer();
    const [refresh, setRefresh] = useState<string | null>(null);
    const storedFilters = getStoredFilters();
    const [nameDraft, setNameDraft] = useState(storedFilters.name || '');
    const [nameFilter, setNameFilter] = useState(storedFilters.name || '');
    const [importanceLevelFilter, setImportanceLevelFilter] = useState<string>(storedFilters.importanceLevel || '');
    const [hasCommercialFilter, setHasCommercialFilter] = useState<boolean>(storedFilters.hasCommercial || false);
    const [areaFilter, setAreaFilter] = useState<string>(storedFilters.area || '');
    const [areas, setAreas] = useState<any[]>([]);
    const [commercialFilter, setCommercialFilter] = useState<string>(storedFilters.commercial || '');
    const [drivers, setDrivers] = useState<any[]>([]);
    const [isAcquiredFilter, setIsAcquiredFilter] = useState<string>(storedFilters.isAcquired || '');
    const { api, state } = useHttpClient();

    const goToMap = () => {
        navigate('/customers/map');
    };

    const handleCreate = () => {
        navigate('/customers/create');
    };

    const handleApplyFilters = () => {
        const trimmedName = nameDraft.trim();
        setNameFilter(trimmedName);
        
        const filters = {
            name: trimmedName,
            importanceLevel: importanceLevelFilter,
            hasCommercial: hasCommercialFilter,
            area: areaFilter,
            commercial: commercialFilter,
            isAcquired: isAcquiredFilter
        };
        
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
        setRefresh(Date.now().toString());
    };

    const handleResetFilters = () => {
        setNameDraft('');
        setNameFilter('');
        setImportanceLevelFilter('');
        setHasCommercialFilter(false);
        setAreaFilter('');
        setCommercialFilter('');
        setIsAcquiredFilter('');
        localStorage.removeItem(FILTERS_STORAGE_KEY);
        setRefresh(Date.now().toString());
    };

    useEffect(() => {
        api('/fleet/areas/', 'GET');
        api('/fleet/drivers/?is_commercial=true', 'GET');
    }, []);

    useEffect(() => {
        if (state.data && !state.loading && state.path?.includes('/fleet/areas/')) {
            setAreas(state.data.results || []);
        }
        if (state.data && !state.loading && state.path?.includes('/fleet/drivers/')) {
            setDrivers(state.data.results || []);
        }
    }, [state]);

    useEffect(() => {
        const filters = {
            name: nameFilter,
            importanceLevel: importanceLevelFilter,
            hasCommercial: hasCommercialFilter,
            area: areaFilter,
            commercial: commercialFilter,
            isAcquired: isAcquiredFilter
        };
        
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    }, [nameFilter, importanceLevelFilter, hasCommercialFilter, areaFilter, commercialFilter, isAcquiredFilter]);

    return (
        <PageBody 
            title={'common.label.customers'} 
            newAction={handleCreate}
            newActionTitle={'common.label.nuevo-customer'}
            secondaryAction={goToMap}
            secondaryActionTitle={'common.label.vista-mapa'}
        >
            <>
                <div className="w-full pb-3">
                    <div className="w-full flex flex-col gap-3">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="w-full sm:max-w-md">
                                <label className="block text-xs font-semibold text-[#000] mb-1">
                                    {t('common.label.nombre')}
                                </label>
                                <div className="relative">
                                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                        <SearchIcon className="h-4 w-4 text-[#006bc6]" />
                                    </span>
                                    <input
                                        type="search"
                                        name="name"
                                        value={nameDraft}
                                        onChange={(e) => setNameDraft(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleApplyFilters();
                                            }
                                        }}
                                        placeholder={t('common.label.nombre')}
                                        className="block w-full h-[40px] rounded-md border border-[#ccc] bg-white pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-[#006bc6] focus:ring-2 focus:ring-[#006bc6]/20"
                                    />
                                </div>
                            </div>
                            <div className="w-full sm:max-w-md">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    {t('common.label.area')}
                                </label>
                                <select
                                    value={areaFilter}
                                    onChange={(e) => setAreaFilter(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                                >
                                    <option value="">{t('common.label.todas')}</option>
                                    {areas.map((area: any) => (
                                        <option key={area.id} value={area.id}>
                                            {area.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="w-full sm:max-w-md">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    {t('common.label.comercial')}
                                </label>
                                <select
                                    value={commercialFilter}
                                    onChange={(e) => setCommercialFilter(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                                >
                                    <option value="">{t('common.label.todos')}</option>
                                    {drivers.map((driver: any) => (
                                        <option key={driver.id} value={driver.id}>
                                            {driver.full_name || driver.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="w-full sm:max-w-md">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    {t('common.label.estado')}
                                </label>
                                <select
                                    value={isAcquiredFilter}
                                    onChange={(e) => setIsAcquiredFilter(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                                >
                                    <option value="">{t('common.label.todos')}</option>
                                    <option value="true">{t('common.form-label.cliente')}</option>
                                    <option value="false">{t('common.label.prospecto')}</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 items-end">
                            <div className="w-full sm:flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('common.label.categoria')}
                                </label>
                                <div className="flex gap-6">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="importance_level_filter"
                                            value=""
                                            checked={importanceLevelFilter === ''}
                                            onChange={(e) => setImportanceLevelFilter(e.target.value)}
                                            className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">{t('common.label.todas')}</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="importance_level_filter"
                                            value="A"
                                            checked={importanceLevelFilter === 'A'}
                                            onChange={(e) => setImportanceLevelFilter(e.target.value)}
                                            className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">A</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="importance_level_filter"
                                            value="B"
                                            checked={importanceLevelFilter === 'B'}
                                            onChange={(e) => setImportanceLevelFilter(e.target.value)}
                                            className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">B</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="radio"
                                            name="importance_level_filter"
                                            value="C"
                                            checked={importanceLevelFilter === 'C'}
                                            onChange={(e) => setImportanceLevelFilter(e.target.value)}
                                            className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">C</span>
                                    </label>
                                    <label className="flex items-center cursor-pointer ml-6">
                                        <input
                                            type="checkbox"
                                            checked={hasCommercialFilter}
                                            onChange={(e) => setHasCommercialFilter(e.target.checked)}
                                            className="w-4 h-4 text-[#006bc6] border-gray-300 rounded focus:ring-[#006bc6]"
                                        />
                                        <span className="ml-2 text-sm font-semibold text-gray-700">{t('common.label.sin-comercial')}</span>
                                    </label>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    outlined
                                    label={t('common.label.filtrar')}
                                    onClick={handleApplyFilters}
                                />
                                <Button
                                    outlined
                                    label={t('common.button.reset')}
                                    onClick={handleResetFilters}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <Table
                    path="/fleet/customers/"
                    deletePath="/fleet/customers/"
                    Form={CustomerForm}
                    doEdit={(item: any) => navigate(`/customers/${item.id}`)}
                    withPagination
                    refresh={refresh}
                    query={[
                        nameFilter ? `name=${encodeURIComponent(nameFilter)}` : '',
                        importanceLevelFilter ? `importance_level=${importanceLevelFilter}` : '',
                        hasCommercialFilter ? 'commercial__isnull=true' : '',
                        areaFilter ? `areas=${areaFilter}` : '',
                        commercialFilter ? `commercial=${commercialFilter}` : '',
                        isAcquiredFilter ? `is_acquired=${isAcquiredFilter}` : ''
                    ].filter(Boolean).join('&')}
                    headers={[
                        {
                            key: 'name',
                            label: 'common.label.nombre'
                        },
                        {
                            key: 'areas',
                            label: 'common.label.areas'
                        },
                        {
                            key: 'importance_level',
                            label: 'common.label.categoria'
                        },
                        {
                            key: 'address',
                            label: 'common.label.direccion',
                            className: 'max-w-xs whitespace-normal break-words'
                        },
                        {
                            key: 'city',
                            label: 'common.label.poblacion'
                        },
                        {
                            key: 'lat',
                            label: 'common.label.latitud',
                            render: (value: number | null) => value ? value.toFixed(6) : '-'
                        },
                        {
                            key: 'lon',
                            label: 'common.label.longitud',
                            render: (value: number | null) => value ? value.toFixed(6) : '-'
                        },
                        {
                            key: 'is_acquired',
                            label: 'common.label.estado',
                            render: (value: boolean) => (
                                <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                                    value 
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-blue-100 text-blue-800'
                                }`}>
                                    {value 
                                        ? t('common.form-label.cliente')
                                        : t('common.label.prospecto')
                                    }
                                </span>
                            )
                        },
                        {
                            key: 'commercial',
                            label: 'common.label.comercial',
                            render: (value: any, item: any) => {
                                if (!value) return '-';
                                if (typeof value === 'object' && value.full_name) {
                                    return value.full_name || value.name || '-';
                                }
                                const driver = drivers.find((d: any) => d.id === value);
                                return driver ? (driver.full_name || driver.name) : '-';
                            }
                        },
                    ]}
                />
            </>
        </PageBody>
    );
};
